import { environment } from "../../environments/environment";

/**
 * Integração com o AWS WAF (Challenge / intelligent threat mitigation).
 *
 * Cenário: front e APIs em domínios diferentes (cross-domain), então o cookie
 * `aws-waf-token` injetado automaticamente NÃO viaja nas chamadas XHR. Por isso
 * usamos o Application Integration SDK do WAF para obter o token e enviá-lo no
 * header `x-aws-waf-token` nas requisições aos hosts protegidos.
 *
 * Para ativar:
 *  1. Console do WAF -> seu Web ACL -> aba "Application integration" -> copie a
 *     "Integration URL" + `challenge.js` (SDK do Challenge silencioso) em `integrationUrl`.
 *  2. Em "Token domains" do Web ACL adicione `mandae.com.br`.
 *  3. O backend precisa liberar o header `x-aws-waf-token` no
 *     `Access-Control-Allow-Headers` (preflight CORS).
 *  4. Ligue `enabled: true` e ajuste `protectedHosts` para os hosts associados
 *     ao Web ACL.
 */

export interface AwsWafConfig {
  enabled: boolean;
  /** Integration URL (challenge.js) do Web ACL. */
  integrationUrl: string;
  /** Hosts cujas requisições devem receber o token do WAF. */
  protectedHosts: string[];
}

interface AwsWafIntegration {
  getToken: () => Promise<string>;
  hasToken?: () => boolean;
  forceRefreshToken?: () => Promise<string>;
}

declare global {
  interface Window {
    AwsWafIntegration?: AwsWafIntegration;
    /** Config exposta para o interceptor legado (AngularJS), que não importa o environment. */
    __mandaeWaf?: AwsWafConfig;
  }
}

const config: AwsWafConfig = (environment as any).awsWaf || {
  enabled: false,
  integrationUrl: "",
  protectedHosts: [],
};

/**
 * Carrega o SDK de integração do WAF (chamado no APP_INITIALIZER).
 * Resolve no onload/onerror, com timeout de segurança para nunca travar o boot.
 */
export function loadAwsWafSdk(): Promise<void> {
  if (!config.enabled || !config.integrationUrl) {
    return Promise.resolve();
  }

  // disponibiliza a config para o interceptor do AngularJS
  window.__mandaeWaf = config;

  return new Promise<void>((resolve) => {
    if (window.AwsWafIntegration) {
      resolve();
      return;
    }

    let settled = false;
    const done = () => {
      if (!settled) {
        settled = true;
        resolve();
      }
    };

    const script = document.createElement("script");
    script.src = config.integrationUrl;
    script.defer = true;
    script.onload = done;
    script.onerror = () => {
      console.error("[AwsWaf] Falha ao carregar o SDK de integração do WAF");
      done();
    };

    // não bloqueia o boot da aplicação além de 5s caso a CDN do WAF demore
    setTimeout(done, 5000);

    document.head.appendChild(script);
  });
}

/** Indica se a URL deve receber o token do WAF. */
export function isWafProtectedUrl(url: string): boolean {
  if (!config.enabled || !url) {
    return false;
  }
  return (config.protectedHosts || []).some((host) => url.indexOf(host) > -1);
}

/** Obtém o token do WAF de forma segura. Retorna null se indisponível. */
export async function getAwsWafToken(): Promise<string | null> {
  if (!config.enabled || !window.AwsWafIntegration) {
    return null;
  }
  try {
    return await window.AwsWafIntegration.getToken();
  } catch (e) {
    console.error("[AwsWaf] Erro ao obter token do WAF", e);
    return null;
  }
}
